import {
  FormControlLabel,
  FormHelperText,
  styled,
  Switch,
  TextField as MuiTextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material'
import { Icon } from '@iconify/react'
import { formatDateForApi } from '../../../../../utils/date'
import { DateInput } from '../../../../components/inputs/DatePicker'
import CalendarIcon from '../../../../components/icons/CalendarIcon'
import MultilineTextEditor from '../../../../components/editors/multiline-text/MultilineTextEditor'
import { colorPalette } from '../../../../style/colorPalette'
import { intl } from '../../../../../i18n'
import { FormState, PRESET_ICONS, PRESET_COLORS } from '../../hooks'

type MilestoneFormFieldsProps = {
  formState: FormState
  onFieldChange: (field: keyof FormState, value: string | boolean) => void
  showAchievedToggle?: boolean
  autoFocusName?: boolean
  dateConflictMilestone?: { name: string } | null
}

export const MilestoneFormFields = ({
  formState,
  onFieldChange,
  showAchievedToggle = true,
  autoFocusName = false,
  dateConflictMilestone,
}: MilestoneFormFieldsProps) => (
  <>
    <div>
      <FieldLabel variant="caption" color="text.secondary">
        {intl.formatMessage({ id: 'milestone.field.name' })} *
      </FieldLabel>
      <TextField
        size="small"
        fullWidth={true}
        value={formState.name}
        onChange={e => onFieldChange('name', e.target.value)}
        placeholder={intl.formatMessage({
          id: 'milestone.placeholder.name',
        })}
        autoFocus={autoFocusName}
      />
    </div>

    <RowContainer>
      <div>
        <FieldLabel variant="caption" color="text.secondary">
          {intl.formatMessage({ id: 'milestone.field.date' })} *
        </FieldLabel>
        <DateInputContainer
          $hasError={!!dateConflictMilestone}
          onClick={e => {
            const input = (e.currentTarget as HTMLElement).querySelector(
              'input'
            )
            input?.focus()
          }}
        >
          <DateInput
            value={
              formState.milestoneDate
                ? new Date(formState.milestoneDate)
                : undefined
            }
            onChange={date => {
              onFieldChange(
                'milestoneDate',
                date ? (formatDateForApi(date) ?? '') : ''
              )
            }}
            placeholder="YYYY/MM/DD"
          />
          <CalendarIconContainer>
            <CalendarIcon />
          </CalendarIconContainer>
        </DateInputContainer>
        {dateConflictMilestone && (
          <FormHelperText error={true}>
            {intl.formatMessage(
              { id: 'milestone.error.sameDateExists' },
              { name: dateConflictMilestone.name }
            )}
          </FormHelperText>
        )}
      </div>

      {showAchievedToggle && (
        <AchievedContainer>
          <FieldLabel variant="caption" color="text.secondary">
            {intl.formatMessage({ id: 'milestone.field.status' })}
          </FieldLabel>
          <AchievedFormControlLabel
            control={
              <Switch
                checked={formState.achieved}
                onChange={e => onFieldChange('achieved', e.target.checked)}
                color="primary"
                size="small"
              />
            }
            label={
              formState.achieved
                ? intl.formatMessage({ id: 'milestone.status.achieved' })
                : intl.formatMessage({ id: 'milestone.status.pending' })
            }
          />
        </AchievedContainer>
      )}
    </RowContainer>

    <RowContainer>
      <div>
        <FieldLabel variant="caption" color="text.secondary">
          {intl.formatMessage({ id: 'milestone.field.icon' })}
        </FieldLabel>
        <ToggleButtonGroup
          exclusive={true}
          size="small"
          value={formState.iconName}
          onChange={(_, v) => v && onFieldChange('iconName', v)}
        >
          {PRESET_ICONS.map(icon => (
            <IconToggleButton key={icon} value={icon}>
              <Icon
                icon={icon}
                width={23}
                height={23}
                color={
                  formState.iconName === icon
                    ? formState.color
                    : colorPalette.monotone[5]
                }
              />
            </IconToggleButton>
          ))}
        </ToggleButtonGroup>
      </div>

      <div>
        <FieldLabel variant="caption" color="text.secondary">
          {intl.formatMessage({ id: 'milestone.field.color' })}
        </FieldLabel>
        <ToggleButtonGroup
          exclusive={true}
          size="small"
          value={formState.color}
          onChange={(_, v) => v && onFieldChange('color', v)}
        >
          {PRESET_COLORS.map(color => (
            <ToggleButton key={color} value={color}>
              <ColorCircle bgcolor={color} />
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </div>
    </RowContainer>

    <div>
      <FieldLabel variant="caption" color="text.secondary">
        {intl.formatMessage({ id: 'milestone.field.description' })}
      </FieldLabel>
      <DescriptionContainer>
        <MultilineTextEditor
          value={formState.description}
          setValue={(value: string) => onFieldChange('description', value)}
          disableSbsMode={true}
        />
      </DescriptionContainer>
    </div>
  </>
)

const RowContainer = styled('div')({
  display: 'flex',
  gap: 24,
  alignItems: 'flex-start',
})

const AchievedContainer = styled('div')({
  display: 'flex',
  flexDirection: 'column',
})

const FieldLabel = styled(Typography)({
  marginBottom: 4,
  display: 'block',
})

const DateInputContainer = styled('div')<{ $hasError?: boolean }>(
  ({ $hasError }) => ({
    width: 180,
    height: 35,
    display: 'flex',
    alignItems: 'center',
    border: `1px solid ${$hasError ? colorPalette.pink[5] : colorPalette.monotone[2]}`,
    borderRadius: 4,
    paddingLeft: 12,
    paddingRight: 12,
    fontSize: 14,
    cursor: 'pointer',
    boxSizing: 'border-box',
    '& input': {
      fontSize: 14,
      color: colorPalette.monotone[9],
    },
    '&:hover': {
      borderColor: $hasError ? colorPalette.pink[5] : colorPalette.skyBlue[7],
    },
    '&:focus-within': {
      borderColor: $hasError ? colorPalette.pink[5] : colorPalette.skyBlue[7],
      borderWidth: 2,
      paddingLeft: 11,
      paddingRight: 11,
    },
  })
)

const CalendarIconContainer = styled('div')({
  marginLeft: 'auto',
  display: 'flex',
  pointerEvents: 'none',
})

const DescriptionContainer = styled('div')({
  height: 300,
  border: `1px solid ${colorPalette.monotone[2]}`,
  borderRadius: 4,
  overflow: 'hidden',
})

const AchievedFormControlLabel = styled(FormControlLabel)({
  marginLeft: 0,
  '& .MuiFormControlLabel-label': {
    fontSize: 14,
    color: colorPalette.monotone[9],
  },
})

const IconToggleButton = styled(ToggleButton)({
  padding: 6,
})

const ColorCircle = styled('div')<{ bgcolor: string }>(({ bgcolor }) => ({
  width: 20,
  height: 20,
  borderRadius: '50%',
  backgroundColor: bgcolor,
}))

const TextField = styled(MuiTextField)({
  '& .MuiOutlinedInput-root': {
    '& fieldset': { borderColor: colorPalette.monotone[2] },
    '&:hover fieldset': { borderColor: colorPalette.skyBlue[7] },
    '&.Mui-focused fieldset': {
      borderColor: colorPalette.skyBlue[7],
      borderWidth: '1px',
    },
  },
  '& .MuiOutlinedInput-input': {
    fontSize: 14,
    color: colorPalette.monotone[9],
    caretColor: colorPalette.skyBlue[7],
    '&:focus': { backgroundColor: colorPalette.blue[0] },
  },
})
